import React, { useState, useCallback } from "react";
import "./GenericModal.css";
import { isEmpty } from "../../Utils/utils";

const defaultModalState = {
  isOpen: false,
  title: "",
  subtitle: "",
  content: null,
  positiveText: "",
  negativeText: "",
  neutralText: "",
  onPositive: null,
  onNegative: null,
  onNeutral: null,
  onClose: null,
  showPositive: false,
  showNegative: false,
  showNeutral: false,
  outSideClickClose: true,
};

export function useGenericModal(initialState = {}) {
  const [genericModal, setGenericModal] = useState({
    ...defaultModalState,
    ...initialState,
  });

  const closeModal = useCallback(() => {
    setGenericModal((prev) => {
      if (typeof prev.onClose === "function") {
        prev.onClose();
      }

      return {
        ...prev,
        isOpen: false,
      };
    });
  }, []);

  const openModal = useCallback((modalData = {}) => {
    setGenericModal({
      ...defaultModalState,
      ...modalData,
      showPositive: !isEmpty(modalData.positiveText) && !!modalData.onPositive,
      showNegative: !isEmpty(modalData.negativeText) && !!modalData.onNegative,
      showNeutral: !isEmpty(modalData.neutralText) && !!modalData.onNeutral,
      isOpen: true,
    });
  }, []);

  const updateModal = useCallback((modalData = {}) => {
    setGenericModal((prev) => ({
      ...prev,
      ...modalData,
    }));
  }, []);

  const resetModal = useCallback(() => {
    setGenericModal(defaultModalState);
  }, []);

  return {
    genericModal,
    setGenericModal,
    openModal,
    closeModal,
    updateModal,
    resetModal,
  };
}

export default function GenericModal({
  isOpen,
  title,
  subtitle,
  content,
  children,
  positiveText = "Confirm",
  negativeText = "Cancel",
  neutralText = "Later",
  onPositive,
  onNegative,
  onNeutral,
  onClose,
  showPositive = true,
  showNegative = true,
  showNeutral = true,
  outSideClickClose = true,
}) {
  if (!isOpen) return null;

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && outSideClickClose && onClose) {
      onClose();
    }
  };

  const handlePositive = () => {
    if (typeof onPositive === "function") onPositive();
  };

  const handleNegative = () => {
    if (typeof onNegative === "function") onNegative();
  };

  const handleNeutral = () => {
    if (typeof onNeutral === "function") onNeutral();
  };

  return (
    <div className="generic-modal-overlay" onClick={handleOverlayClick}>
      <div className="generic-modal-card" role="dialog" aria-modal="true">
        {
            outSideClickClose && (<button className="generic-modal-close" onClick={onClose} aria-label="Close modal">
          ×
        </button>)
        }

        {title && <h2 className="generic-modal-title">{title}</h2>}
        {subtitle && <p className="generic-modal-subtitle">{subtitle}</p>}

        {(content || children) && (
          <div className="generic-modal-content">
            {content || children}
          </div>
        )}

        <div className="generic-modal-actions">
          {showNegative && (
            <button className="generic-modal-btn negative" onClick={handleNegative}>
              {negativeText}
            </button>
          )}

          {showNeutral && (
            <button className="generic-modal-btn neutral" onClick={handleNeutral}>
              {neutralText}
            </button>
          )}

          {showPositive && (
            <button className="generic-modal-btn positive" onClick={handlePositive}>
              {positiveText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* Example usage:

import GenericModal, { useGenericModal } from "./GenericModal";

function App() {
  const {
    genericModal,
    openModal,
    closeModal,
  } = useGenericModal();

  const handleOpenModal = () => {
    openModal({
      title: "Delete this item?",
      subtitle: "This action cannot be undone. The title and subtitle can be long and will wrap correctly.",
      content: <p>Please confirm your action.</p>,
      positiveText: "Confirm",
      negativeText: "Delete",
      neutralText: "Later",
      onPositive: () => {
        console.log("Positive clicked");
        closeModal();
      },
      onNegative: () => {
        console.log("Negative clicked");
        closeModal();
      },
      onNeutral: () => {
        console.log("Neutral clicked");
        closeModal();
      },
      onClose: () => {
        console.log("Modal closed");
      },
      outSideClickClose: true,
    });
  };

  return (
    <>
      <button onClick={handleOpenModal}>Open Modal</button>

      <GenericModal
        {...genericModal}
        onClose={closeModal}
      />
    </>
  );
}

*/

